import React, { Fragment } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { roleShortLabel } from './roles';

export { ROLE_LABELS, roleLabel, roleShortLabel, isApprovalRole, ACCESS_ROLE_KEYS } from './roles';

export const cx = (...classes) => classes.filter(Boolean).join(' ');

/* ------------------------------------------------------------------ status */

// The six states a request can be in. Colour carries the meaning here and
// nowhere else in the app — brand oxblood is reserved for actions.
export const STATUS = {
    Draft: { label: 'Draft', dot: 'bg-neutral-400', chip: 'bg-neutral-100 text-neutral-700 ring-neutral-200' },
    Submitted: { label: 'Awaiting approval', dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800 ring-amber-200' },
    'Partially Approved': { label: 'In progress', dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800 ring-amber-200' },
    'Fully Approved': { label: 'Approved', dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-800 ring-emerald-200' },
    Denied: { label: 'Denied', dot: 'bg-red-500', chip: 'bg-red-50 text-red-700 ring-red-200' },
    Cancelled: { label: 'Cancelled', dot: 'bg-neutral-300', chip: 'bg-neutral-50 text-neutral-500 ring-neutral-200' },
};

export const StatusBadge = ({ status, className }) => {
    const s = STATUS[status] || STATUS.Submitted;
    return (
        <span
            className={cx(
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset',
                s.chip,
                className
            )}
        >
            <span className={cx('h-1.5 w-1.5 rounded-full', s.dot)} />
            {s.label}
        </span>
    );
};

/* ------------------------------------------------------------ going stale */

// A request stays visible for two weeks after the event is over, so there's
// time to close it out, then drops off the working lists.
export const ARCHIVE_AFTER_DAYS = 14;

/** Every date a request covers. A recurring event is one request, many dates. */
export const occurrencesOf = (form) => {
    const dates = Array.isArray(form.occurrenceDates) && form.occurrenceDates.length
        ? form.occurrenceDates
        : (form.dayDateOfEvent ? [form.dayDateOfEvent] : []);
    return [...dates].sort();
};

/** The last day the event actually happens — a series isn't over until then. */
export const lastEventDate = (form) => {
    const dates = occurrencesOf(form);
    return dates.length ? dates[dates.length - 1] : null;
};

/** Whole days since the event finished. Negative means it hasn't yet. */
export const daysSinceEvent = (form) => {
    const last = lastEventDate(form);
    if (!last) return null;

    const end = new Date(`${last}T00:00:00`);
    if (Number.isNaN(end.getTime())) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.round((today - end) / 86400000);
};

/** Over, and old enough to stop showing in the day-to-day lists. */
export const isArchived = (form) => {
    const days = daysSinceEvent(form);
    return days !== null && days > ARCHIVE_AFTER_DAYS;
};

// Works off the raw Parse fields so every screen agrees on what a form's
// state is, instead of each one re-deriving it slightly differently.
export const deriveStatus = (form) => {
    if (form.isDraft) return 'Draft';
    if (form.isCancelled) return 'Cancelled';
    if (form.isDenied) return 'Denied';
    if (form.isFullyApproved) return 'Fully Approved';
    if ((form.completedApprovals || []).length > 0) return 'Partially Approved';
    return 'Submitted';
};

/* ----------------------------------------------------- the approval rail */

// The one thing this whole tool exists to answer: who still has to sign off?
// Each required role is a link in a chain; the first unsigned link is the
// blocker and is the only one drawn in full contrast.
export const ApprovalRail = ({ required = [], completed = [], denied = false, className }) => {
    if (!required.length) {
        return <span className={cx('text-xs text-neutral-400', className)}>No approvals required</span>;
    }

    const done = new Set(completed);
    const blockerIndex = required.findIndex((r) => !done.has(r));

    return (
        <div className={cx('flex flex-wrap items-center gap-x-1 gap-y-1.5', className)}>
            {required.map((role, i) => {
                const isDone = done.has(role);
                const isBlocker = !denied && i === blockerIndex;
                return (
                    <React.Fragment key={role}>
                        {i > 0 && <span aria-hidden="true" className="h-px w-2 bg-neutral-200" />}
                        <span
                            className={cx(
                                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xxs font-medium ring-1 ring-inset',
                                isDone && 'bg-emerald-50 text-emerald-700 ring-emerald-200',
                                isBlocker && 'bg-amber-50 text-amber-800 ring-amber-300',
                                !isDone && !isBlocker && 'bg-white text-neutral-400 ring-neutral-200'
                            )}
                            title={isDone ? `${roleShortLabel(role)} — approved` : `${roleShortLabel(role)} — not yet approved`}
                        >
                            {isDone && (
                                <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none" aria-hidden="true">
                                    <path d="M2.5 6.5 5 9l4.5-6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                            {roleShortLabel(role)}
                        </span>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export const ApprovalCount = ({ required = [], completed = [] }) => {
    if (!required.length) return null;
    const done = required.filter((r) => (completed || []).includes(r)).length;
    return (
        <span className="text-xs tabular-nums text-neutral-500">
            {done} of {required.length} approved
        </span>
    );
};

/* ----------------------------------------------------------------- button */

const BUTTON_VARIANTS = {
    primary: 'bg-brand-700 text-white shadow-sm hover:bg-brand-800 disabled:bg-brand-300',
    secondary: 'bg-white text-neutral-800 ring-1 ring-inset ring-neutral-300 shadow-sm hover:bg-neutral-50',
    ghost: 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
    danger: 'bg-white text-red-700 ring-1 ring-inset ring-red-200 shadow-sm hover:bg-red-50',
};

const BUTTON_SIZES = {
    sm: 'px-2.5 py-1.5 text-xs',
    md: 'px-3.5 py-2 text-sm',
};

export const Button = ({ variant = 'secondary', size = 'md', className, as: As = 'button', ...props }) => (
    <As
        className={cx(
            'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors',
            'disabled:cursor-not-allowed disabled:opacity-60',
            BUTTON_VARIANTS[variant],
            BUTTON_SIZES[size],
            className
        )}
        {...props}
    />
);

/* ------------------------------------------------------------ page header */

export const PageHeader = ({ title, description, actions, children }) => (
    <header className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
            <div className="min-w-0">
                <h1 className="font-display text-2xl font-semibold tracking-tight text-neutral-900">{title}</h1>
                {description && <p className="mt-1 text-sm text-neutral-500">{description}</p>}
            </div>
            {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </div>
        {children}
    </header>
);

export const PageBody = ({ className, children }) => (
    <div className={cx('mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8', className)}>{children}</div>
);

/* -------------------------------------------------------------- surfaces */

// Spreads the rest of its props so callers can set an id — the long form's
// section rail links to them as in-page anchors.
export const Card = ({ className, children, ...props }) => (
    <div className={cx('rounded-lg border border-neutral-200 bg-white shadow-sm', className)} {...props}>
        {children}
    </div>
);

export const CardHeader = ({ title, description, actions }) => (
    <div className="flex items-center justify-between gap-4 border-b border-neutral-200 px-5 py-3.5">
        <div className="min-w-0">
            <h2 className="text-sm font-semibold text-neutral-900">{title}</h2>
            {description && <p className="mt-0.5 text-xs text-neutral-500">{description}</p>}
        </div>
        {actions}
    </div>
);

export const EmptyState = ({ title, description, action }) => (
    <div className="px-6 py-14 text-center">
        <p className="font-display text-base font-semibold text-neutral-900">{title}</p>
        {description && <p className="mx-auto mt-1 max-w-sm text-sm text-neutral-500">{description}</p>}
        {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
);

/* --------------------------------------------------------------- feedback */

export const Spinner = ({ className }) => (
    <svg className={cx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
);

export const LoadingOverlay = ({ message = 'Working…' }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 px-4" role="status" aria-live="polite">
        <div className="flex items-center gap-3 rounded-lg bg-white px-5 py-4 shadow-xl">
            <Spinner className="h-5 w-5 text-brand-700" />
            <span className="text-sm font-medium text-neutral-800">{message}</span>
        </div>
    </div>
);

/* ------------------------------------------------------------------ modal */

export const Modal = ({ open, onClose, title, description, children, footer }) => (
    <Transition show={open} as={Fragment}>
        <Dialog onClose={onClose} className="relative z-50">
            <TransitionChild
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 bg-neutral-900/40" />
            </TransitionChild>

            <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
                <div className="flex min-h-full items-center justify-center">
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 translate-y-2 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-2 sm:scale-95"
                    >
                        <DialogPanel className="w-full max-w-lg rounded-xl bg-white shadow-xl">
                            <div className="border-b border-neutral-200 px-6 py-4">
                                <DialogTitle className="font-display text-lg font-semibold text-neutral-900">
                                    {title}
                                </DialogTitle>
                                {description && <p className="mt-0.5 text-sm text-neutral-500">{description}</p>}
                            </div>
                            <div className="px-6 py-5">{children}</div>
                            {footer && (
                                <div className="flex justify-end gap-2 border-t border-neutral-200 px-6 py-4">
                                    {footer}
                                </div>
                            )}
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </Transition>
);

const BANNER_TONES = {
    info: 'bg-neutral-50 text-neutral-700 ring-neutral-200',
    warning: 'bg-amber-50 text-amber-900 ring-amber-200',
    danger: 'bg-red-50 text-red-800 ring-red-200',
    success: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
};

export const Banner = ({ tone = 'info', title, children, className }) => (
    <div className={cx('rounded-md px-4 py-3 text-sm ring-1 ring-inset', BANNER_TONES[tone], className)}>
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cx(title && 'mt-0.5')}>{children}</div>}
    </div>
);
